import { memo, useState, useEffect, useRef } from 'react'
import { cn } from '../../../../lib/utils'

interface TypewriterTextProps {
  text: string
  isActive: boolean
  speed?: number
  onComplete?: () => void
  className?: string
  as?: 'h2' | 'p' | 'span'
}

export const TypewriterText = memo(function TypewriterText({
  text,
  isActive,
  speed = 30,
  onComplete,
  className,
  as: Component = 'span',
}: TypewriterTextProps) {
  const [displayedText, setDisplayedText] = useState('')
  const [isComplete, setIsComplete] = useState(false)
  const hasStartedRef = useRef(false)
  const onCompleteRef = useRef(onComplete)

  useEffect(() => {
    onCompleteRef.current = onComplete
  }, [onComplete])

  useEffect(() => {
    if (!isActive || !text || hasStartedRef.current) return

    hasStartedRef.current = true
    let currentIndex = 0

    const interval = setInterval(() => {
      if (currentIndex < text.length) {
        setDisplayedText(text.slice(0, currentIndex + 1))
        currentIndex++
      } else {
        clearInterval(interval)
        setIsComplete(true)
        onCompleteRef.current?.()
      }
    }, speed)

    return () => clearInterval(interval)
  }, [isActive, text, speed])

  useEffect(() => {
    if (!isActive) {
      hasStartedRef.current = false
      setDisplayedText('')
      setIsComplete(false)
    }
  }, [isActive])

  if (!isActive && !isComplete) {
    return null
  }

  return (
    <Component className={cn(className)}>
      {displayedText}
    </Component>
  )
})

TypewriterText.displayName = 'TypewriterText'
